/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { stopEvent } from "app/client/lib/domUtils";
import { loadUserManager } from "app/client/lib/imports";
import { isKnetEmbed, openInKueTab } from "app/client/lib/knetEmbed";
import { makeT } from "app/client/lib/localization";
import { getTimeFromNow } from "app/client/lib/timeUtils";
import { docUrl, urlState } from "app/client/models/gristUrlState";
import { HomeItem, HomeModel, ViewSettings } from "app/client/models/HomeModel";
import { getWorkspaceInfo, workspaceName } from "app/client/models/WorkspaceInfo";
import { contextMenu } from "app/client/ui/contextMenu";
import { stripIconFromName } from "app/client/ui/DocIcon";
import { STICKY_HEADER_HEIGHT_PX } from "app/client/ui/DocMenuCss";
import { downloadDocModal } from "app/client/ui/MakeCopyMenu";
import { showRenameDocModal } from "app/client/ui/RenameDocModal";
import { shadowScroll } from "app/client/ui/shadowScroll";
import { makeShareDocUrl } from "app/client/ui/ShareMenu";
import {
  isNarrowScreenObs,
  mediaMedium,
  theme,
  vars,
} from "app/client/ui2018/cssVars";
import { IconName } from "app/client/ui2018/IconList";
import { icon as cssIcon } from "app/client/ui2018/icons";
import { knetDocTile, knetTypeToIconId } from "app/client/ui2018/knetIcons";
import { menu, menuIcon, menuItem, select } from "app/client/ui2018/menus";
import { confirmModal, saveModal } from "app/client/ui2018/modals";
import { stretchedLink } from "app/client/ui2018/stretchedLink";
import { buildTabs, TabProps } from "app/client/ui2018/tabs";
import { unstyledButton, unstyledH2, unstyledUl } from "app/client/ui2018/unstyled";
import { visuallyHidden } from "app/client/ui2018/visuallyHidden";
import { HomePageTab } from "app/common/gristUrls";
import { SortPref } from "app/common/Prefs";
import * as roles from "app/common/roles";
import { Document, Workspace } from "app/common/UserAPI";

import {
  Computed,
  computedArray,
  Disposable,
  dom,
  makeTestId,
  MaybeObsArray,
  Observable,
  styled,
  subscribe,
} from "grainjs";
import sortBy from "lodash/sortBy";

const t = makeT("DocList");

const testId = makeTestId("test-dm-");

interface DocListOptions {
  home: HomeModel;
  viewSettings?: ViewSettings;
}

export class DocList extends Disposable {
  private readonly _home = this._options.home;
  private readonly _tabNames: Computed<HomePageTab[]> = Computed.create(
    this,
    this._home.currentPage,
    (_use, page) => {
      if (page === "all") {
        return ["projects", "recent", "pinned", "all", "activity"];
      }
      else {
        return ["recent", "all", "pinned"];
      }
    },
  );

  private readonly _tabIconsAndLabels = getTabIconsAndLabels();

  private readonly _tabs: MaybeObsArray<TabProps> = this.autoDispose(
    computedArray(this._tabNames, tab => ({
      id: tab,
      label: this._tabIconsAndLabels[tab].label,
      icon: this._tabIconsAndLabels[tab].icon,
      link: { homePageTab: tab },
    }),
    ));

  private readonly _viewSettings =
    this._options.viewSettings ?? this._options.home;

  private readonly _tab = Computed.create(
    this,
    this._tabNames,
    urlState().state,
    (_use, tabs, state) => {
      const { homePageTab } = state as any;
      return homePageTab && (tabs as any).includes(homePageTab) ? homePageTab : tabs[0];
    },
  );

  private readonly _showWorkspace = Computed.create(
    this,
    this._home.currentPage,
    (_use, page) => {
      return page !== "workspace";
    },
  );

  constructor(private _options: DocListOptions) {
    super();
    // Clear workspace filter whenever the user leaves the "recent" tab.
    this.autoDispose(subscribe(this._tab, (_use, tab) => {
      if (tab !== "recent") {
        this._home.workspaceFilter.set(null);
      }
    }));
  }

  public buildDom() {
    return dom("div", this._buildHeader(), this._buildBody());
  }

  private _buildHeader() {
    return cssHeader(
      visuallyHidden(unstyledH2(t("Documents list"))),
      buildTabs(this._tabs, this._tab),
      this._buildViewSettings(),
    );
  }

  private _buildViewSettings() {
    return dom.maybe(
      use => use(this._tab) !== "recent" && use(this._tab) !== "activity",
      () =>
        cssViewSettings(
          dom.update(
            select<SortPref>(
              this._viewSettings.currentSort as any,
              [
                { value: "name", label: t("Sort by name") },
                { value: "date", label: t("Sort by date") },
              ],
              { buttonCssClass: cssSortSelect.className },
            ),
            testId("sort-mode"),
          ),
        ),
    );
  }

  private _buildBody() {
    return dom.domComputed(this._tab, (tab) => {
      if (tab === "activity") {
        return this._buildActivityFeed();
      }
      if (tab === "projects") {
        return this._buildWorkspaceList();
      }
      return this._buildDocumentList();
    });
  }

  private _buildWorkspaceList() {
    return [
      cssDocHeaderRow(
        dom.hide(isNarrowScreenObs()),
        cssNameColumn(t("Workspace Name"), { "aria-hidden": "true" }),
        cssWorkspaceColumn(t("Docs"), { "aria-hidden": "true" }),
        cssEditedAtColumn(t("Last Updated"), { "aria-hidden": "true" }),
        cssOptionsColumn(),
      ),
      cssDocListContainer(
        dom.forEach(Computed.create(null, (use) => {
          const sorted = [...use(this._home.workspaces)].filter(w => !w.isSupportWorkspace);
          return sortBy(sorted, (w: Workspace) => !getWorkspaceInfo(this._home.app, w).isDefault);
        }), (ws) => {
          const updatedAt = getUpdatedAt(ws);
          return cssDocRow(
            cssDoc(
              cssDocIconAndName(
                knetDocTile("workspace", testId("workspace-icon")),
                cssDocNameAndBadges(
                  cssDocName(
                    { href: "#" },
                    dom.on("click", (ev) => {
                      ev.preventDefault();
                      this._home.workspaceFilter.set(ws.id);
                      urlState().pushUrl(prev => ({ org: prev.org, homePageTab: "recent" as any })).catch(() => {});
                    }),
                    ws.name,
                    testId("workspace-name"),
                  ),
                ),
              ),
              cssDocWorkspace(
                dom("span", ws.docs.length.toString()),
                testId("workspace-doc-count"),
              ),
              cssDocEditedAt(
                updatedAt,
                testId("workspace-edited-at"),
              ),
              cssDocDetailsCompact(
                cssDocName(
                  { href: "#" },
                  dom.on("click", (ev) => {
                    ev.preventDefault();
                    this._home.workspaceFilter.set(ws.id);
                    urlState().pushUrl({ homePageTab: "recent" }).catch(() => {});
                  }),
                  ws.name,
                ),
                cssDocEditedAt(updatedAt),
              ),
            ),
          );
        }),
      ),
    ];
  }

  private _buildDocumentList() {
    const { currentSort } = this._viewSettings;
    return [
      dom.domComputed(
        use => ({
          docs: use(this._home.currentWSDocs),
          sort: use(currentSort),
          tab: use(this._tab),
        }),
        ({ docs, sort, tab }) => {
          docs = sortAndFilterDocs(docs, { sort, tab });
          if (docs.length === 0) {
            return cssNoDocsMessage(
              cssNoDocsImage({ alt: "", width: "180", height: "124", src: "img/create-document.svg" }),
              dom("div", cssParagraph(t("Your tables and documents will appear here."))),
              testId("no-docs-message"),
            );
          }

          return [
            cssDocHeaderRow(
              dom.hide(isNarrowScreenObs()),
              cssNameColumn(t("Name"), { "aria-hidden": "true" }),
              cssWorkspaceColumn(t("Type"), dom.show(this._showWorkspace), { "aria-hidden": "true" }),
              cssEditedAtColumn(t("Last edited"), { "aria-hidden": "true" }),
              cssOptionsColumn(),
            ),
            cssDocListContainer(
              dom.forEach(docs, (item) => {
                const isDoc = item.type === "doc";
                const doc = item.doc;
                const updatedAt = getUpdatedAt(item);

                return cssDocRow(
                  cssDoc(
                    cssDoc.cls("-no-access", isDoc && doc ? (doc.disabledAt !== undefined || !roles.canView(doc.access)) : false),
                    cssDocIconAndName(
                      knetDocTile(knetTypeToIconId(item.type), testId("doc-icon")),
                      cssDocNameAndBadges(
                        cssDocName(
                          {
                            href: isDoc && doc ? urlState().makeUrl(docUrl(doc)) : item.href,
                            target: isKnetEmbed() ? undefined : "_blank",
                          },
                          dom.on("click", (ev) => {
                            if (isKnetEmbed()) {
                              ev.preventDefault();
                              ev.stopPropagation();
                              const url = isDoc && doc ? urlState().makeUrl(docUrl(doc)) : item.href;
                              const name = isDoc && doc ? stripIconFromName(doc.name, Boolean(doc.options?.appearance?.icon?.emoji)) : item.name;
                              if (url) { openInKueTab(url, name); }
                            }
                          }),
                          isDoc && doc ? stripIconFromName(doc.name, Boolean(doc.options?.appearance?.icon?.emoji)) : item.name,
                          testId("doc-name"),
                        ),
                        cssDocBadges(
                          item.isPinned ?
                            cssPinIcon("Pin2", testId("doc-pinned")) :
                            null,
                          item.public ?
                            cssWorldIcon("World", testId("doc-public")) :
                            null,
                        ),
                      ),
                    ),
                    cssDocWorkspace(
                      dom.show(this._showWorkspace),
                      dom("span",
                        visuallyHidden(t("Workspace")),
                        item.workspaceName,
                      ),
                      testId("doc-workspace"),
                    ),
                    cssDocEditedAt(
                      updatedAt,
                      testId("doc-edited-at"),
                    ),
                    cssDocDetailsCompact(
                      cssDocName(
                        {
                          href: isDoc && doc ? urlState().makeUrl(docUrl(doc)) : item.href,
                          target: isKnetEmbed() ? undefined : "_blank",
                        },
                        dom.on("click", (ev) => {
                          if (isKnetEmbed()) {
                            ev.preventDefault();
                            ev.stopPropagation();
                            const url = isDoc && doc ? urlState().makeUrl(docUrl(doc)) : item.href;
                            const name = isDoc && doc ? stripIconFromName(doc.name, Boolean(doc.options?.appearance?.icon?.emoji)) : item.name;
                            if (url) { openInKueTab(url, name); }
                          }
                        }),
                        isDoc && doc ? stripIconFromName(doc.name, Boolean(doc.options?.appearance?.icon?.emoji)) : item.name,
                      ),
                      cssDocEditedAt(updatedAt),
                      cssDocBadges(
                        !item.isPinned ?
                          null :
                          cssPinIcon("Pin2"),
                        !item.public ?
                          null :
                          cssWorldIcon("World"),
                      ),
                    ),
                    // Only show menu options if there are any menu items for this type
                    (() => {
                      const hasMenuItems = item.type === "doc" ||
                        ["bot", "collection", "knet-agent", "knet-flow", "gis-project"].includes(item.type);

                      if (!hasMenuItems) {
                        return null;
                      }

                      return [
                        cssDocOptions(
                          cssDotsIcon("Dots"),
                          menu(() => makeItemOptionsMenu(this._home, item), {
                            placement: "bottom-start",
                            parentSelectorToMark: "." + cssDocRow.className,
                          }),
                          dom.on("click", ev => stopEvent(ev)),
                          { "aria-label": t("context menu - {{- documentName }}", { documentName: `"${item.name}"` }) },
                          testId("doc-options"),
                        ),
                        contextMenu(() => makeItemOptionsMenu(this._home, item), {
                          parentSelectorToMark: "." + cssDocRow.className,
                        }),
                      ];
                    })(),
                    testId("doc"),
                  ),
                );
              }),
            ),
          ];
        },
      ),
    ];
  }

  private _buildActivityFeed() {
    const mockActions = [
      { agent: "Data Analyst", action: "Thinking", detail: "Analyzing spreadsheet patterns", time: "2m ago" },
      { agent: "Data Analyst", action: "Searching Knowledge Base", detail: "Looking for sales data Q4", time: "5m ago" },
      { agent: "Legal Bot", action: "Drafting Email", detail: "Sumarizing contract clauses", time: "12m ago" },
      { agent: "Navigator", action: "Mapping", detail: "Calculating optimal route", time: "1h ago" },
    ];

    return cssActivityContainer(
      cssActivityHeader(
        dom("div", t("Agent Activity Log")),
        cssUndoButton(cssIcon("Undo"), t("Undo Last Action")),
      ),
      cssDocListContainer(
        mockActions.map(a =>
          cssActivityRow(
            cssActionIcon(a.action === "Thinking" ? "Robot" : a.action.includes("Search") ? "Search" : "Script"),
            cssActionContent(
              cssActionMain(
                cssAgentName(a.agent),
                cssActionType(a.action),
              ),
              cssActionDetail(a.detail),
            ),
            cssActionTime(a.time),
          ),
        ),
      ),
    );
  }
}

export function makeDocOptionsMenu(home: HomeModel, doc: Document) {
  const org = home.app.currentOrg;
  const orgAccess: roles.Role | null = org ? org.access : null;
  const isElectron = (window as any).isRunningUnderElectron;

  function deleteDoc() {
    confirmModal(
      t("Delete {{name}}", { name: doc.name }),
      t("Delete"),
      () => home.deleteDoc(doc.id, false).catch(reportError),
      { explanation: t("Document will be moved to Trash.") },
    );
  }

  async function manageUsers() {
    const api = home.app.api;
    const user = home.app.currentUser;
    (await loadUserManager()).showUserManagerModal(api, {
      permissionData: api.getDocAccess(doc.id),
      activeUser: user,
      resourceType: "document",
      resourceId: doc.id,
      resource: doc,
      linkToCopy: makeShareDocUrl(doc),
      reload: () => api.getDocAccess(doc.id),
      onSave: () => home.updateWorkspaces(),
      appModel: home.app,
    });
  }

  return [
    menuItem(
      () => showRenameDocModal({ home, doc }),
      t("Rename and set icon"),
      dom.cls("disabled", doc.disabledAt !== undefined || !roles.isOwner(doc)),
      testId("rename-doc"),
    ),
    menuItem(
      () => showMoveDocModal(home, doc),
      t("Move"),
      // Note that moving the doc requires ACL access on the doc. Moving a doc to a workspace
      // that confers descendant ACL access could otherwise increase the user's access to the doc.
      // By requiring the user to have ACL edit access on the doc to move it prevents using this
      // as a tool to gain greater access control over the doc.
      // Having ACL edit access on the doc means the user is also powerful enough to remove
      // the doc, so this is the only access check required to move the doc out of this workspace.
      // The user must also have edit access on the destination, however, for the move to work.
      dom.cls("disabled", doc.disabledAt !== undefined || !roles.canEditAccess(doc.access)),
      testId("move-doc"),
    ),
    menuItem(
      deleteDoc,
      t("Delete"),
      dom.cls("disabled", !roles.isOwner(doc)),
      testId("delete-doc"),
    ),
    menuItem(
      () => home.pinUnpinDoc(doc.id, !doc.isPinned).catch(reportError),
      doc.isPinned ? t("Unpin") : t("Pin"),
      dom.cls("disabled", !roles.canEdit(orgAccess)),
      testId("pin-doc"),
    ),
    menuItem(
      manageUsers,
      roles.canEditAccess(doc.access) ? t("Manage users") : t("Access details"),
      testId("doc-access"),
    ),
    // The electron method for "downloading" documents only works
    // with a websocket currently, so downloads are only easy
    // to support when the document is open.
    // TODO: support showItemInFolder with electron in a better way.
    (isElectron ? null :
      menuItem(
        () => downloadDocModal(doc, home.app),
        menuIcon("Download"), t("Download document..."),
        dom.cls("disabled", doc.disabledAt !== undefined),
        testId("tb-share-option"))
    ),
  ];
}

/**
 * Create options menu for any HomeItem type (docs, bots, collections, agents, flows, etc.)
 */
export function makeItemOptionsMenu(home: HomeModel, item: HomeItem) {
  const org = home.app.currentOrg;
  const orgAccess: roles.Role | null = org ? org.access : null;

  // For documents, use the existing doc menu
  if (item.type === "doc" && item.doc) {
    return makeDocOptionsMenu(home, item.doc);
  }

  // For other types, provide a simpler menu with pin/unpin
  const menuItems: any[] = [];

  // Add pin/unpin for supported types
  const supportsPinning = ["bot", "collection", "knet-agent", "knet-flow", "gis-project"].includes(item.type);

  if (supportsPinning) {
    const isPinned = Boolean(item.isPinned);
    menuItems.push(
      menuItem(
        () => home.pinUnpinItem(item, !isPinned).catch(reportError),
        isPinned ? t("Unpin") : t("Pin"),
        dom.cls("disabled", !roles.canEdit(orgAccess)),
        testId("pin-item"),
      ),
    );
  }

  // Add type-specific menu items here in the future
  // For example: rename, delete, etc.

  return menuItems;
}

function showMoveDocModal(home: HomeModel, doc: Document) {
  saveModal((_ctl, owner) => {
    const selected: Observable<number | null> = Observable.create(owner, null);
    const body = cssMoveDocModalBody(
      shadowScroll(
        dom.forEach(home.workspaces, (ws) => {
          if (ws.isSupportWorkspace) {
            return null;
          }
          const isCurrent = Boolean(ws.docs.find(_doc => _doc.id === doc.id));
          const isEditable = roles.canEdit(ws.access);
          const disabled = isCurrent || !isEditable;
          return cssMoveDocListItem(
            cssMoveDocListText(workspaceName(home.app, ws)),
            isCurrent ? cssMoveDocListHintText(t("Current workspace")) : null,
            !isEditable ?
              cssMoveDocListHintText(t("Requires edit permissions")) :
              null,
            cssMoveDocListItem.cls("-disabled", disabled),
            cssMoveDocListItem.cls(
              "-selected",
              use => use(selected) === ws.id,
            ),
            dom.on("click", () => disabled || selected.set(ws.id)),
            testId("dest-ws"),
          );
        }),
      ),
    );
    return {
      title: t("Move {{name}} to workspace", { name: doc.name }),
      body,
      saveDisabled: Computed.create(owner, use => !use(selected)),
      saveFunc: async () =>
        !selected.get() ||
        home.moveDoc(doc.id, selected.get()!).catch(reportError),
      saveLabel: t("Move"),
    };
  });
}

interface IconAndLabel {
  icon: IconName;
  label: string;
}

function getTabIconsAndLabels(): Record<HomePageTab, IconAndLabel> {
  return {
    projects: {
      icon: "Folder",
      label: t("Active Workspaces"),
    },
    recent: {
      icon: "Clock",
      label: t("Recent Activities"),
    },
    pinned: {
      icon: "Pin2",
      label: t("Pinned"),
    },
    all: {
      icon: "Layers",
      label: t("All"),
    },
    activity: {
      icon: "Clock",
      label: t("Agent Activity Log"),
    },
  };
}

interface SortAndFilterOptions {
  sort: SortPref;
  tab: HomePageTab;
}

function sortAndFilterDocs(
  docs: HomeItem[],
  { sort, tab }: SortAndFilterOptions,
) {
  if (tab === "pinned") {
    docs = docs.filter(({ isPinned }) => isPinned);
  }
  if (sort === "date" || tab === "recent") {
    docs = sortBy(docs, doc => doc.removedAt || doc.updatedAt).reverse();
  }
  else {
    docs = sortBy(docs, doc => doc.name.toLowerCase());
  }
  return docs;
}

export async function renameDoc(home: HomeModel, doc: Document, val: string) {
  if (val !== doc.name) {
    try {
      await home.renameDoc(doc.id, val);
    }
    catch (err) {
      reportError(err as Error);
    }
  }
}

export function getUpdatedAt(item: { updatedAt: string, removedAt?: string }) {
  const removedAt = (item as any).removedAt;
  const updatedAt = (item as any).updatedAt;
  if (removedAt) {
    return t("Deleted {{at}}", { at: getTimeFromNow(removedAt) });
  }
  if (!updatedAt) { return ""; }
  return t("Edited {{at}}", { at: getTimeFromNow(updatedAt) });
}

const cssHeader = styled("div", `
  position: sticky;
  top: ${STICKY_HEADER_HEIGHT_PX}px;
  background-color: ${theme.mainPanelBg};
  z-index: ${vars.stickyHeaderZIndex};
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 24px;
  margin-bottom: 12px;
  padding: 4px 0px;
`);

const cssViewSettings = styled("div", `
  display: flex;
  align-items: center;
  min-width: 0;
`);

const cssSortSelect = styled("div", `
  border: 1px solid color-mix(in srgb, ${theme.controlBorder} 50%, transparent);
  border-radius: 12px;
  display: inline-flex;
  column-gap: 6px;
  align-items: center;
  color: ${theme.text};
  --icon-color: ${theme.lightText};
  background-color: color-mix(in srgb, ${theme.hover} 35%, transparent);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.01em;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover, &:focus, &.weasel-popup-open {
    background-color: ${theme.hover};
    color: ${theme.text};
    --icon-color: ${theme.text};
    outline: none;
    box-shadow: none;
  }
`);

const cssParagraph = styled("div", `
  color: ${theme.text};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.6;
  margin-bottom: 12px;
  text-align: center;
`);

const cssNoDocsMessage = styled("div", `
  margin-top: 70px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  align-items: center;
  justify-content: center;
`);

const cssNoDocsImage = styled("img", `
  display: flex;
  align-items: center;
  justify-content: center;
`);

const cssDocHeaderRow = styled("div", `
  display: flex;
  padding: 12px 18px 6px 18px;
  color: ${theme.lightText};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.85;

  @media ${mediaMedium} {
    & {
      display: none;
    }
  }
`);

const cssNameColumn = styled("div", `
  flex: 0 0 45%;
  min-width: 0;
  overflow: hidden;
`);

const cssWorkspaceColumn = styled("div", `
  flex: 0 0 25%;
  min-width: 0;
  overflow: hidden;
`);

const cssEditedAtColumn = styled("div", `
  flex: 0 0 22%;
  min-width: 0;
`);

const cssOptionsColumn = styled("div", `
  flex: none;
  width: 44px;
  margin: 0 4px 0 auto;
  padding: 4px;
  display: flex;
`);

// Apple Inset Grouped list container with continuous curvature squircle and specular bevel
const cssDocListContainer = styled(unstyledUl, `
  overflow: hidden;
  border-radius: 20px;
  border: 2px solid color-mix(in srgb, ${theme.controlBorder} 40%, transparent);
  background: ${theme.mainPanelBg};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 8px 24px -6px rgba(0, 0, 0, 0.05);
  margin-top: 8px;
`);

const cssDocRow = styled("li", `
  position: relative;
  font-size: 14px;
  color: ${theme.text};
  --icon-color: ${theme.lightText};
  border-bottom: 1px solid color-mix(in srgb, ${theme.controlBorder} 25%, transparent);
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover, &.weasel-popup-open {
    background-color: color-mix(in srgb, ${theme.hover} 35%, transparent);
  }
`);

const cssDoc = styled("div", `
  display: flex;
  position: relative;
  align-items: center;
  outline: none;
  padding: 10px 16px;
  min-height: 60px;

  &-no-access, &-no-access:hover, &-no-access:focus {
    color: ${theme.disabledText};
    cursor: not-allowed;
  }

  @media ${mediaMedium} {
    & {
      align-items: initial;
      column-gap: 16px;
    }
  }
`);

const cssDocDetailsCompact = styled("div", `
  display: none;
  flex: 1 1 auto;
  flex-direction: column;
  row-gap: 8px;
  column-gap: 40px;
  min-width: 0px;
  align-items: flex-start;

  @media ${mediaMedium} {
    & {
      display: flex;
    }
  }
`);

const cssDocIconAndName = styled(cssNameColumn, `
  display: flex;
  align-items: center;
  column-gap: 11px;
  overflow: hidden;
  min-width: 0;

  @media ${mediaMedium} {
    & {
      align-items: initial;
      flex: none;
    }
  }
`);

const cssDocNameAndBadges = styled("div", `
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  overflow: hidden;
  min-width: 0;

  @media ${mediaMedium} {
    & {
      display: none;
    }
  }
`);

const noAccessStyles = `
  .${cssDoc.className}-no-access &,
  .${cssDoc.className}-no-access:hover &,
  .${cssDoc.className}-no-access:focus & {
    color: ${theme.disabledText};
  }
`;

const cssDocName = styled(stretchedLink, `
  font-size: 14px;
  flex: 0 1 auto;
  padding: 5px;
  color: ${theme.text};
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &, &:hover, &:focus {
    text-decoration: none;
    outline: none;
    color: inherit;
  }

  &:focus-visible {
    outline-offset: -3px;
  }

  ${noAccessStyles}

  .${cssDocDetailsCompact.className} & {
    padding: 0;
  }

  .${cssDocDetailsCompact.className} &:focus-visible {
    outline-offset: 3px;
  }

  @media ${mediaMedium} {
    & {
      width: 100%;
    }
  }
`);

const cssDocBadges = styled("div", `
  display: flex;
  align-items: center;
  flex-shrink: 0;
  column-gap: 8px;
  margin-left: auto;
  min-height: 16px;

  @media ${mediaMedium} {
    & {
      margin-left: initial;
    }
  }
`);

const cssPinIcon = styled(cssIcon, `
  flex: none;
  --icon-color: ${theme.lightText};
`);

const cssWorldIcon = styled(cssIcon, `
  width: 24px;
  height: 24px;
  flex: none;
  --icon-color: ${theme.accentIcon};
`);

const cssActivityContainer = styled("div", `
  margin-top: 24px;
  background: ${theme.mainPanelBg};
  border: 1px solid ${theme.controlBorder};
  border-radius: 8px;
  overflow: hidden;
`);

const cssActivityHeader = styled("div", `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: ${theme.hover};
  font-weight: 600;
  border-bottom: 1px solid ${theme.controlBorder};
`);

const cssUndoButton = styled(unstyledButton, `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #FF6B6B;
  color: white;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  
  &:hover {
    background: #FF5252;
  }
`);

const cssActivityRow = styled("li", `
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid ${theme.controlBorder};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: ${theme.lightHover};
  }
`);

const cssActionIcon = styled(cssIcon, `
  width: 24px;
  height: 24px;
  --icon-color: ${theme.accentIcon};
`);

const cssActionContent = styled("div", `
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`);

const cssActionMain = styled("div", `
  display: flex;
  gap: 8px;
  align-items: center;
`);

const cssAgentName = styled("span", `
  font-weight: 600;
  color: ${theme.text};
`);

const cssActionType = styled("span", `
  font-size: 12px;
  background: ${theme.accentIcon};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
`);

const cssActionDetail = styled("div", `
  font-size: 13px;
  color: ${theme.lightText};
`);

const cssActionTime = styled("div", `
  font-size: 12px;
  color: ${theme.lightText};
  white-space: nowrap;
`);

const secondaryColumnStyles = `
  color: ${theme.mediumText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: normal;
  ${noAccessStyles}

  @media ${mediaMedium} {
    .${cssDoc.className} > & {
      display: none;
    }
  }
`;

const cssDocWorkspace = styled(cssWorkspaceColumn, secondaryColumnStyles);

const cssDocEditedAt = styled(cssEditedAtColumn, `
  ${secondaryColumnStyles};

  @media ${mediaMedium} {
    & {
      width: 100%;
    }
  }
`);

const cssDocOptions = styled(unstyledButton, `
  position: relative;
  z-index: 2; /* make sure this is above the stretched link row */
  flex: none;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 4px 0 auto;
  height: 32px;
  width: 32px;
  padding: 4px;
  line-height: 0px;
  border-radius: 8px;
  --icon-color: ${theme.lightText};
  transition: all 0.15s ease;

  &:hover, &.weasel-popup-open {
    background-color: ${theme.hover};
    --icon-color: ${theme.text};
  }
`);

const cssDotsIcon = styled(cssIcon, `
  --icon-color: inherit;
  width: 18px;
  height: 18px;
`);

const cssMoveDocModalBody = styled("div", `
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${theme.modalBorderDark};
  margin: 0 -64px;
  height: 200px;
`);

const cssMoveDocListItem = styled("div", `
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 32px;
  padding: 12px 64px;
  cursor: pointer;
  font-size: ${vars.mediumFontSize};

  &-selected {
    background-color: ${theme.moveDocsSelectedBg};
    color: ${theme.moveDocsSelectedFg};
  }
  &-disabled {
    color: ${theme.moveDocsDisabledFg};
    cursor: default;
  }
`);

const cssMoveDocListText = styled("div", `
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  justify-content: center;
`);

const cssMoveDocListHintText = styled(cssMoveDocListText, `
  text-align: right;
`);
